/**
 * showcaseScore.ts — a Pokémon's PokéStop Showcase score, as the game
 * computes it (2026-09-16 owner ask: "Showcase score on size Pokémon").
 *
 * THE FORMULA (Pokémon GO Hub research for the three components, checked
 * against a 0.84 m / 16.54 kg Squirtle with 20 IV points that scored 910
 * BEFORE the XXL bonus existed; the bonus from bmenrigh's datamining of
 * October 2023, carried by the pokemondb and pokexperience calculators):
 *
 *   score = (heightRatio / xxlCeiling)        * 800
 *         + (weightRatio / weightCeiling)      * 150
 *         + (ivSum / 45)                       *  50
 *         + (XXL ? 178 : 0)
 *
 * where heightRatio is the Pokémon's height over the species' average, and
 * the ceilings are the species' XXL maximums. Height is 80% of the score,
 * which is why an XXL wins showcases. The flat 178 (7300/41, the gap
 * between the best XL and the worst XXL of the 1.55 class) is Niantic's
 * fudge so an XXL always beats an XL of the same species; it applies at the
 * XXL threshold, 1.5x the species' average height.
 *
 * THE GAME'S OWN BADGE DECIDES XXL. When a scan read the size badge
 * (record.sizeClass), that is what the game will pay the bonus on, whatever
 * our species table makes of the ratio: the averages come from the Game
 * Master and a form or a stale row can put a real XXL a hair under 1.5x.
 * Only a record with no badge falls back to the ratio.
 *
 * LEGENDARIES AND ULTRA BEASTS ARE ORDINARY. A day's worth of the owner's
 * real scores (2026-09-19) put Nihilego, Regirock and Stakataka on this
 * formula at the 1.55 class to the point: a 5.5 m / 874.49 kg / 15-12-15
 * Stakataka scored 641 where the formula says 641. A blanket "no size
 * classes" rule was tried for them and is wrong.
 *
 * A RANGE, NOT A POINT (2026-09-19 Showcases page): the game prints height
 * and weight rounded to two decimals, so the true score sits in a band. The
 * low / high bounds run the formula at the rounding edges (+-0.005 m and
 * +-0.005 kg); the point score is the printed values as read.
 *
 * THE CEILING IS PER SPECIES AND NOT IN THE GAME MASTER. Every species tops
 * out at 1.55x, 1.75x or 2.00x its average height (weight 2.05x / 2.25x /
 * 2.50x to match), and which one is a per-species choice that does not
 * follow from the average (Diglett 2.0x, Joltik 1.55x, Squirtle 1.75x).
 * KNOWN_XXL_CEILINGS holds the ones read off GO Hub's database by hand; every
 * other species is scored with the common 1.75x and flagged as an estimate
 * (`ceilingKnown: false`), which the UI prints with a tilde. Add a species
 * to the table when its ceiling is confirmed; the estimate's error is at
 * most about 12% of the height points.
 *
 * Pure module. No DOM, no I/O.
 */
import type { MyPokemonRecord } from "./myPokemonStore";
import { speciesAverageSize } from "./speciesSize";

export type XxlCeiling = 1.55 | 1.75 | 2.0;

export const SHOWCASE_HEIGHT_POINTS = 800;
export const SHOWCASE_WEIGHT_POINTS = 150;
export const SHOWCASE_IV_POINTS = 50;
/** The flat bonus every XXL entry gets (bmenrigh, 2023-10: 7300 / 41). */
export const SHOWCASE_XXL_BONUS = 178;
/** Height ratio at and above which a Pokémon is XXL (speciesSize.sizeClassForRatio). */
export const SHOWCASE_XXL_RATIO = 1.5;
/** The three components at their ceilings plus the XXL bonus. Weight can overflow a little. */
export const SHOWCASE_MAX_POINTS =
  SHOWCASE_HEIGHT_POINTS + SHOWCASE_WEIGHT_POINTS + SHOWCASE_IV_POINTS + SHOWCASE_XXL_BONUS;
/** The game prints metres and kilograms to two decimals. */
const PRINT_HALF_STEP = 0.005;

/** The weight ceiling that pairs with each height ceiling. */
const WEIGHT_CEILING: Record<XxlCeiling, number> = { 1.55: 2.05, 1.75: 2.25, 2.0: 2.5 };

/** The most common ceiling; the estimate for species not in the table. */
export const DEFAULT_XXL_CEILING: XxlCeiling = 1.75;

/**
 * Per-species XXL height ceilings, read off db.pokemongohub.net on
 * 2026-09-16 (each species page prints its XXL range). Keyed by dex number.
 */
export const KNOWN_XXL_CEILINGS: Readonly<Record<number, XxlCeiling>> = {
  7: 1.75, // Squirtle
  10: 1.75, // Caterpie
  25: 1.75, // Pikachu
  41: 1.75, // Zubat
  50: 2.0, // Diglett
  95: 1.75, // Onix
  130: 2.0, // Gyarados
  133: 1.75, // Eevee
  // Read off real in-game scores from the owner's Rock showcase, 2026-09-19:
  // each fits exactly one ceiling with the +178 bonus (showcaseScore.test).
  139: 2.0, // Omastar (1.89 m / 73.96 kg / XXL = 1096)
  476: 1.75, // Probopass (2.2 m / 544.79 kg / XXL = 1044)
  688: 1.75, // Binacle (0.85 m / 49.65 kg / XXL = 1102)
  793: 1.55, // Nihilego (1.6 m / 94.68 kg / XL = 849: an Ultra Beast on the standard formula)
  377: 1.55, // Regirock (2.09 m / 385.28 kg = 794; 2.13 m / 439.73 kg / XL = 832: a Legendary, standard)
  805: 1.55, // Stakataka (5.5 m / 874.49 kg / 15-12-15 = 641 to the point; lucky 4.19 m / 374.34 kg = 472)
  143: 1.75, // Snorlax
  149: 1.75, // Dragonite
  229: 1.75, // Houndoom
  249: 1.75, // Lugia
  320: 2.0, // Wailmer
  321: 2.0, // Wailord
  383: 1.55, // Groudon
  384: 1.55, // Rayquaza
  595: 1.55, // Joltik
};

export interface ShowcaseScore {
  /** The rounded total at the printed height and weight. */
  score: number;
  /** The total at the rounding edges: the true score is in [low, high]. */
  scoreLow: number;
  scoreHigh: number;
  /** XXL (height ratio >= 1.5): the flat bonus applies. */
  xxl: boolean;
  bonusPts: number;
  heightPts: number;
  /** Null when the record has no weight read (scored as zero). */
  weightPts: number | null;
  /** Null when the record has no IVs (scored as zero). */
  ivPts: number | null;
  /** Height over the species' average. */
  heightRatio: number;
  /** Weight over the species' average, or null. */
  weightRatio: number | null;
  ceiling: XxlCeiling;
  /** False when the species' ceiling is the 1.75x estimate. */
  ceilingKnown: boolean;
  /** The species' three-component maximum, for "of N". */
  maxScore: number;
}

export function xxlCeilingFor(speciesId: number | null | undefined): { ceiling: XxlCeiling; known: boolean } {
  const known = speciesId != null ? KNOWN_XXL_CEILINGS[speciesId] : undefined;
  return known ? { ceiling: known, known: true } : { ceiling: DEFAULT_XXL_CEILING, known: false };
}

/**
 * Score a record. Null when there is no height, or no species average to
 * place it against: the height is 80% of the score and the one thing that
 * cannot be scored as zero.
 */
export function showcaseScoreFor(
  r: Pick<MyPokemonRecord, "speciesId" | "formId" | "heightM" | "weightKg" | "ivAtk" | "ivDef" | "ivHp"> &
    Partial<Pick<MyPokemonRecord, "sizeClass">>,
): ShowcaseScore | null {
  if (r.heightM == null || !(r.heightM > 0)) return null;
  const avg = speciesAverageSize(r.speciesId, r.formId);
  if (!avg) return null;
  const { ceiling, known } = xxlCeilingFor(r.speciesId);
  const weightCeiling = WEIGHT_CEILING[ceiling];
  const ivPts =
    r.ivAtk != null && r.ivDef != null && r.ivHp != null
      ? ((r.ivAtk + r.ivDef + r.ivHp) / 45) * SHOWCASE_IV_POINTS
      : null;
  const hasWeight = r.weightKg != null && r.weightKg > 0;
  const heightRatio = r.heightM / avg.heightM;
  // The badge the game showed wins; the ratio only speaks when there is none.
  const badge = r.sizeClass ?? null;
  const xxl = badge != null ? badge === "xxl" : heightRatio >= SHOWCASE_XXL_RATIO;
  // The formula at one (height, weight) pair, unrounded.
  const at = (h: number, w: number | null) => {
    const hr = h / avg.heightM;
    const hp = (hr / ceiling) * SHOWCASE_HEIGHT_POINTS;
    const wp = w != null ? ((w / avg.weightKg) / weightCeiling) * SHOWCASE_WEIGHT_POINTS : 0;
    const xxlAt = badge != null ? xxl : hr >= SHOWCASE_XXL_RATIO;
    return hp + wp + (ivPts ?? 0) + (xxlAt ? SHOWCASE_XXL_BONUS : 0);
  };
  const heightPts = (heightRatio / ceiling) * SHOWCASE_HEIGHT_POINTS;
  const weightRatio = hasWeight ? (r.weightKg as number) / avg.weightKg : null;
  const weightPts = weightRatio != null ? (weightRatio / weightCeiling) * SHOWCASE_WEIGHT_POINTS : null;
  const total = at(r.heightM, hasWeight ? (r.weightKg as number) : null);
  const w = hasWeight ? (r.weightKg as number) : null;
  const low = at(Math.max(r.heightM - PRINT_HALF_STEP, 0.001), w != null ? Math.max(w - PRINT_HALF_STEP, 0.001) : null);
  const high = at(r.heightM + PRINT_HALF_STEP, w != null ? w + PRINT_HALF_STEP : null);
  return {
    score: Math.round(total),
    scoreLow: Math.round(Math.min(low, high)),
    scoreHigh: Math.round(Math.max(low, high)),
    xxl,
    bonusPts: xxl ? SHOWCASE_XXL_BONUS : 0,
    heightPts: Math.round(heightPts),
    weightPts: weightPts != null ? Math.round(weightPts) : null,
    ivPts: ivPts != null ? Math.round(ivPts) : null,
    heightRatio,
    weightRatio,
    ceiling,
    ceilingKnown: known,
    maxScore: SHOWCASE_MAX_POINTS,
  };
}

/** "812" or "~812" when the species' ceiling is estimated. */
export function formatShowcaseScore(s: ShowcaseScore): string {
  return `${s.ceilingKnown ? "" : "~"}${s.score}`;
}

/** "1,081 to 1,094", or the single figure when rounding cannot move it. */
export function formatShowcaseRange(s: ShowcaseScore): string {
  if (s.scoreLow === s.scoreHigh) return s.scoreLow.toLocaleString();
  return `${s.scoreLow.toLocaleString()} to ${s.scoreHigh.toLocaleString()}`;
}
